import { CareerCategory, SegmentedBarChart, SegmentedBarSegment } from '@backrs_ui/common-ui';

import { CareerPathWithScore } from '@backrs_ui/grow-ui/src/models/models';
import { filterCareerPathSuggestionsByScoreSection } from '@backrs_ui/grow-ui/src/utils/careerPath/careerPathSuggestions';

import { Box, Typography, SxProps, Theme } from '@mui/material';

type CareerCategorySegmentsProps = Readonly<{
  careerCategories: CareerCategory[];
  careerPathSuggestions: CareerPathWithScore[] | undefined;
  onCategorySelect: (categoryId: string) => void;
  selectedCategoryId?: string;
}>;

export const CareerCategorySegments = ({
  careerCategories,
  careerPathSuggestions,
  onCategorySelect,
  selectedCategoryId,
}: CareerCategorySegmentsProps) => {
  const handleCategoryClick = (categoryId: string) => {
    onCategorySelect(categoryId);
  };

  const getCategorySegments = (category: CareerCategory): SegmentedBarSegment[] => {
    if (!careerPathSuggestions) {
      return [];
    }

    // Filter career paths for this category
    const categoryCareerPaths = careerPathSuggestions.filter(
      (suggestion) => suggestion.careerPath.career_category_id === category.id,
    );

    if (categoryCareerPaths.length === 0) {
      return [];
    }

    // Count paths by score section
    const topPaths = filterCareerPathSuggestionsByScoreSection(categoryCareerPaths, 'TOP');
    const middlePaths = filterCareerPathSuggestionsByScoreSection(categoryCareerPaths, 'MIDDLE');
    const lowPaths = filterCareerPathSuggestionsByScoreSection(categoryCareerPaths, 'LOW');

    const segments: SegmentedBarSegment[] = [];

    if (topPaths.length > 0) {
      segments.push({
        value: topPaths.length,
        color: 'Brand/700',
      });
    }

    if (middlePaths.length > 0) {
      segments.push({
        value: middlePaths.length,
        color: 'Brand/500',
      });
    }

    if (lowPaths.length > 0) {
      segments.push({
        value: lowPaths.length,
        color: 'Brand/200',
      });
    }

    return segments;
  };

  const getGlobalMaxValue = (): number => {
    if (!careerPathSuggestions) return 0;

    return Math.max(
      ...careerCategories.map(
        (category) =>
          careerPathSuggestions.filter(
            (suggestion) => suggestion.careerPath.career_category_id === category.id,
          ).length,
      ),
    );
  };

  return (
    <Box sx={containerStyle}>
      {/* Category Segments */}
      <Box sx={categoriesContainerStyle}>
        {careerCategories.map((category) => {
          const segments = getCategorySegments(category);
          const isSelected = selectedCategoryId === category.id.toString();

          return (
            <Box
              key={category.id}
              id={`career-category-segment-${category.id}`}
              sx={categoryItemStyle(isSelected)}
              onClick={() => handleCategoryClick(category.id.toString())}
            >
              <Typography
                variant="text-xs-semibold"
                color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-secondary']}
                sx={categoryNameStyle}
              >
                {category.short_name}
              </Typography>

              {segments.length > 0 ? (
                <SegmentedBarChart segments={segments} maxValue={getGlobalMaxValue()} height={24} />
              ) : (
                <Box sx={emptyBarStyle} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const categoriesContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const categoryItemStyle =
  (isSelected: boolean): SxProps<Theme> =>
  ({ backrs }) =>
    ({
      display: 'flex',
      alignItems: 'center',
      padding: `${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-md']}`,
      borderRadius: backrs.radius['radius-md'],
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      border: `2px solid ${isSelected ? backrs.colors['Brand/500'] : 'transparent'}`,
    }) as const;

const categoryNameStyle: SxProps<Theme> = {
  minWidth: '120px',
} as const;

const emptyBarStyle: SxProps<Theme> = ({ backrs }) =>
  ({
    width: '200px',
    height: '16px',
    backgroundColor: backrs.colors['Colors/Background/bg-quaternary'],
    borderRadius: backrs.spacing['spacing-xs'],
  }) as const;
